import { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';
import { useMutation, useQuery } from 'react-query';

import { API_URL } from 'api/API';
import getLatestSkycamApi from 'api/skyw/getLatestSkycamApi';
import getSkycamImageApi from 'api/skyw/getSkycamImageApi';

import { SkywCommonProps } from 'libs/types';
import { MEDIA_SIZE_LIST } from 'libs/const';

import UpdateLabel from './UpdateLabel';
import Delay from './Delay';
import { checkImage } from 'libs/utils';

const WARN_TIME = 10 * 60;

const SkycamWrapper = styled.a`
  display: block;
  position: relative;
  border-radius: 100%;
  box-shadow: 0 0 20px 10px rgba(0, 0, 0, 1);
  overflow: hidden;
  background-color: #000000;

  img {
    position: relative;
    width: 100%;
    /* cursor: pointer; */
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;

  img:nth-child(1) {
    width: 100%;
  }
  img:nth-child(2) {
    width: 100%;
    position: absolute;
    z-index: 2;
  }
`;

const Point = styled.span`
  position: absolute;
  color: ${(props) => props.theme.skyw.mainColor2};
  font-size: 13px;
  font-weight: 700;
`;

const LastUpdateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
`;

const LastUpdate = styled.div`
  display: flex;
  align-items: center;
  width: 220px;

  & > * {
    white-space: nowrap;
  }
`;

const LastUpdateTime = styled.span`
  font-size: 14px;
  margin-right: 4px;
`;

const Container = styled.div`
  position: relative;
  flex: 1;

  @media screen and (max-width: ${`${MEDIA_SIZE_LIST[0]}px`}) {
    ${LastUpdateTime} {
      font-size: 12px;
    }
    ${LastUpdate} {
      width: 190px;
    }
  }

  @media screen and (max-width: ${`${MEDIA_SIZE_LIST[1]}px`}) {
    ${SkycamWrapper} {
      max-width: 500px;
      margin: 0 auto;
    }
  }
`;

const Skycam = ({ obsId }: SkywCommonProps) => {
  const [imageStatus, setImageStatus] = useState<boolean>();
  const { data } = useQuery([API_URL.SKYW.GET_LATEST_SKYCAM, obsId], () => getLatestSkycamApi({ obsId }), {
    refetchInterval: 1000 * 30,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  });
  const { mutate } = useMutation(
    [API_URL.SKYW.GET_SKYCAM_IMAGE, data?.skycam_path],
    () => getSkycamImageApi({ url: process.env.REACT_APP_API_URL! + data?.skycam_path }),
    {
      onSuccess: (blob) => {
        const fileName = data?.skycam_path.split('/').at(-1)!;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName!;
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 60000);
      },
    }
  );

  const skycamPos = useMemo(() => {
    if (obsId === 1) {
      return {
        top: '-20px',
      };
    } else if (obsId === 2) {
      return {
        top: '-12px',
        left: '-3px',
      };
    } else if (obsId === 3) {
      return {
        top: '-28px',
        left: '-2px',
      };
    } else if (obsId === 4) {
      return {
        top: '-8px',
        left: '-14px',
      };
    } else if (obsId === 5) {
      return {
        top: '-14px',
        left: '-4px',
      };
    } else if (obsId === 0) {
      return {
        top: '-10px',
        left: '10px',
      };
    }
    return {
      top: '-10px',
    };
  }, [obsId]);

  const onImageDownload = () => {
    if (!data?.skycam_path) return;
    mutate();
  };

  useEffect(() => {
    (async () => {
      if (data?.skycam_path) {
        const ok = await checkImage(`${process.env.REACT_APP_API_URL}${data.skycam_path}`);
        setImageStatus(ok);
      }
    })();
  }, [data?.skycam_path]);

  return (
    <Container>
      {data?.result ? (
        <>
          <SkycamWrapper>
            <img
              style={{ ...skycamPos }}
              src={Boolean(imageStatus) ? `${process.env.REACT_APP_API_URL}${data.skycam_path}` : "/assets/error_image.png"}
              alt={`skycam`}
              onClick={onImageDownload}
            />
            <Point style={{ top: 0, left: '50%', transform: 'translateX(-50%)' }}>N</Point>
            <Point style={{ top: '50%', left: 0, transform: 'translateY(-50%)' }}>E</Point>
            <Point style={{ top: '50%', right: 0, transform: 'translateY(-50%)' }}>W</Point>
            <Point style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>S</Point>
          </SkycamWrapper>
          <LastUpdateWrapper>
            <UpdateLabel>LAST UPDATE</UpdateLabel>
            <LastUpdate>
              <LastUpdateTime>
                {data.last_update.date.replaceAll('-', '.')}, {data.last_update.time}
              </LastUpdateTime>
              <Delay obsId={obsId} date={data.last_update.date} time={data.last_update.time} warnTime={WARN_TIME} />
            </LastUpdate>
          </LastUpdateWrapper>
        </>
      ) : (
        <LoadingWrapper>
          <img src="/assets/skyw/blueline.png" alt="skycam_loading" />
          <img src="/assets/skyw/loading.gif" alt="skycam_loading" />
        </LoadingWrapper>
      )}
    </Container>
  );
};

export default Skycam;
